'use client';

import { c as _c } from "react/compiler-runtime";
import type { PrV2StackNode, PrV2StackPullRequest } from '@/lib/pr-v2/types';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Layers } from 'lucide-react';
import Link from 'next/link';
import type { ReactElement } from 'react';
import { useLayoutEffect, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import type { PrV2StackStatusPresentation } from './pr-v2-stack-status';
import { getPrV2StackStatus, getPrV2StackStatusPresentation } from './pr-v2-stack-status';
import { handleLinkListNavigation } from './shared/link-list-navigation';
import { usePrNavigation } from '@/components/providers/pr-navigation';
import { KeyboardShortcut } from '@/components/ui/keyboard-shortcut';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { BetterTooltip } from '@/components/ui/tooltip';
import { seedPrV2Stack, usePrV2Stack, usePrV2StackReviews } from '@/data/v2/pr/use-pr-v2-stack';
import { cn } from '@/lib/utils';
import { useAuth } from '@/providers/auth-provider';
import { buildPrPath } from '@/utils/app-paths';
type PrV2StackDropdownProps = {
  owner: string;
  repo: string;
  number: string;
  /**
   * The /code session panel gets no `S`, `[` or `]` keys or their hints, because `[` and `]` would
   * open the PR page and leave /code. The panel can be narrow on a wide screen, so its status strip
   * follows the PR tab's `@container/pr-tab` instead of the screen, at the `@2xl` width where the
   * tab's header puts the actions beside the title.
   */
  inSessionPanel?: boolean;
};

/** One PR of the stack, in pre-order (root first). */
type PrV2StackEntry = {
  pullRequest: PrV2StackPullRequest;
  parentNumber: number | null;
  firstChildNumber: number | null;
  /**
   * One entry per branching ancestor: the ancestor whose children that level connects, and whether
   * it has later children, keeping vertical connector lines alive for deeper branch rows.
   */
  trail: Array<{
    ancestorNumber: number;
    continues: boolean;
  }>;
};
const MAX_STRIP_SEGMENTS = 12;
const STRIP_BREAKPOINT = {
  page: {
    icon: 'md:hidden',
    strip: 'md:flex'
  },
  sessionPanel: {
    icon: '@2xl/pr-tab:hidden',
    strip: '@2xl/pr-tab:flex'
  }
} as const;
/** The step buttons' `w-7`. */
const NEXT_BUTTON_WIDTH_PX = 28;
const LINE_CLASS = 'absolute left-1/2 w-px bg-zinc-700';
const RAIL_BUTTON_CLASS = 'flex items-center justify-center text-zinc-400 transition-colors hover:bg-border hover:text-foreground focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50';

// Chains read cleaner on one straight line, so only a PR with several children indents them.
function flattenStack(node: PrV2StackNode, parentNumber: number | null = null, trail: PrV2StackEntry['trail'] = []): PrV2StackEntry[] {
  return [{
    pullRequest: node.pullRequest,
    parentNumber,
    firstChildNumber: node.children[0]?.pullRequest.number ?? null,
    trail
  }, ...node.children.flatMap((child, index) => flattenStack(child, node.pullRequest.number, node.children.length > 1 ? [...trail, {
    ancestorNumber: node.pullRequest.number,
    continues: index < node.children.length - 1
  }] : trail))];
}
function LineDot(t0) {
  const $ = _c(12);
  const {
    above,
    below,
    dotClassName
  } = t0;
  let t1;
  if ($[0] !== above) {
    t1 = above ? <span className={cn(LINE_CLASS, "top-0 h-1/2")} /> : null;
    $[0] = above;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== below) {
    t2 = below ? <span className={cn(LINE_CLASS, "bottom-0 h-1/2")} /> : null;
    $[2] = below;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== dotClassName) {
    t3 = cn("relative rounded-full", dotClassName);
    $[4] = dotClassName;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <span className={t3} />;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t4) {
    t5 = <span className="relative flex w-3.5 shrink-0 items-center justify-center self-stretch">{t1}{t2}{t4}</span>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
function StackRow(t0) {
  const $ = _c(42);
  const {
    entry,
    href,
    isCurrent,
    numberColumnWidth,
    presentation,
    onOpen
  } = t0;
  const {
    pullRequest,
    trail
  } = entry;
  const {
    label
  } = presentation;
  const startsBranch = trail.at(-1)?.ancestorNumber === entry.parentNumber;
  let t1;
  if ($[0] !== onOpen) {
    t1 = event => {
      event.preventDefault();
      onOpen();
    };
    $[0] = onOpen;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = isCurrent ? "page" : undefined;
  const t3 = `pr-v2-stack-row-${pullRequest.number}`;
  const t4 = isCurrent && "bg-zinc-900";
  let t5;
  if ($[2] !== t4) {
    t5 = cn("flex h-8 items-center rounded-md pl-1 pr-2.5 outline-hidden hover:bg-zinc-800/60 focus-visible:bg-zinc-800/60", t4);
    $[2] = t4;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== startsBranch || $[5] !== trail) {
    let t7;
    if ($[7] !== startsBranch || $[8] !== trail.length) {
      t7 = (t8, level) => {
        const {
          ancestorNumber,
          continues
        } = t8;
        return <span key={ancestorNumber} className="relative w-3.5">{continues ? <span className={cn(LINE_CLASS, "inset-y-0")} /> : null}{startsBranch && level === trail.length - 1 ? <span className="absolute left-1/2 top-0 h-1/2 w-3.5 rounded-bl-md border-b border-l border-zinc-700" /> : null}</span>;
      };
      $[7] = startsBranch;
      $[8] = trail.length;
      $[9] = t7;
    } else {
      t7 = $[9];
    }
    t6 = trail.map(t7);
    $[4] = startsBranch;
    $[5] = trail;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const t7 = !startsBranch;
  const t8 = entry.firstChildNumber !== null;
  let t9;
  if ($[10] !== isCurrent || $[11] !== presentation.dotClassName) {
    t9 = isCurrent ? "size-2.5 bg-white shadow-[0_0_0_3px_rgb(250_250_250/0.14)]" : cn("size-2", presentation.dotClassName);
    $[10] = isCurrent;
    $[11] = presentation.dotClassName;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== t7 || $[14] !== t8 || $[15] !== t9) {
    t10 = <LineDot above={t7} below={t8} dotClassName={t9} />;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10 || $[18] !== t6) {
    t11 = <span aria-hidden="true" className="flex shrink-0 self-stretch">{t6}{t10}</span>;
    $[17] = t10;
    $[18] = t6;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== numberColumnWidth) {
    t12 = {
      width: numberColumnWidth
    };
    $[20] = numberColumnWidth;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const t13 = `#${pullRequest.number}`;
  let t14;
  if ($[22] !== t12 || $[23] !== t13) {
    t14 = <span className="ml-2.5 mr-3 shrink-0 text-xs tabular-nums text-muted-foreground" style={t12}>{t13}</span>;
    $[22] = t12;
    $[23] = t13;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  const t15 = isCurrent && "font-medium text-white";
  let t16;
  if ($[25] !== t15) {
    t16 = cn("min-w-0 flex-1 truncate text-[13px] text-zinc-300", t15);
    $[25] = t15;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  const t17 = pullRequest.title || `Pull request #${pullRequest.number}`;
  let t18;
  if ($[27] !== t16 || $[28] !== t17) {
    t18 = <span className={t16}>{t17}</span>;
    $[27] = t16;
    $[28] = t17;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] !== label) {
    t19 = label ? <span className={cn("ml-3.5 flex shrink-0 items-center gap-1.5 text-xs", label.className)}><label.Icon className={cn("size-3.5", label.iconClassName)} aria-hidden="true" />{label.text}</span> : null;
    $[30] = label;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  let t20;
  if ($[32] !== href || $[33] !== t1 || $[34] !== t11 || $[35] !== t14 || $[36] !== t18 || $[37] !== t19 || $[38] !== t2 || $[39] !== t3 || $[40] !== t5) {
    t20 = <Link href={href} onNavigate={t1} aria-current={t2} data-testid={t3} className={t5}>{t11}{t14}{t18}{t19}</Link>;
    $[32] = href;
    $[33] = t1;
    $[34] = t11;
    $[35] = t14;
    $[36] = t18;
    $[37] = t19;
    $[38] = t2;
    $[39] = t3;
    $[40] = t5;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  return t20;
}
function RailTooltip(t0) {
  const $ = _c(8);
  const {
    label,
    shortcut,
    children
  } = t0;
  let t1;
  if ($[0] !== shortcut) {
    t1 = shortcut ? <KeyboardShortcut>{shortcut}</KeyboardShortcut> : null;
    $[0] = shortcut;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label || $[3] !== t1) {
    t2 = <div className="flex items-center gap-2">{label}{t1}</div>;
    $[2] = label;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== children || $[6] !== t2) {
    t3 = <BetterTooltip content={t2}>{children}</BetterTooltip>;
    $[5] = children;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
export function PrV2StackDropdown(t0) {
  const $ = _c(81);
  const {
    owner,
    repo,
    number,
    inSessionPanel: t1
  } = t0;
  const inSessionPanel = t1 === undefined ? false : t1;
  const [open, setOpen] = useState(false);
  const listRef = useRef(null);
  const {
    navigate,
    pendingHref
  } = usePrNavigation();
  const {
    user
  } = useAuth();
  const queryClient = useQueryClient();
  const stackQuery = usePrV2Stack(owner, repo, number);
  const stackRoot = stackQuery.data?.isPartOfStack ? stackQuery.data.stackRoot : null;
  const entries = stackRoot ? flattenStack(stackRoot) : [];
  const pullRequestsWithReviews = usePrV2StackReviews(owner, repo, entries.map(_temp));
  let t2;
  if ($[0] !== number) {
    t2 = entry_0 => entry_0.pullRequest.number === Number(number);
    $[0] = number;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const current = entries.find(t2);
  let t3;
  if ($[2] !== owner || $[3] !== repo) {
    t3 = prNumber => buildPrPath({
      owner,
      repo,
      number: prNumber
    });
    $[2] = owner;
    $[3] = repo;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const hrefOf = t3;
  const stepOrigin = entries.find(entry_1 => hrefOf(entry_1.pullRequest.number) === pendingHref) ?? current;
  const stepOriginRef = useRef(stepOrigin);
  useLayoutEffect(() => {
    stepOriginRef.current = stepOrigin;
  });
  let t4;
  if ($[5] !== stackQuery) {
    t4 = nextOpen => {
      setOpen(nextOpen);
      if (nextOpen) {
        stackQuery.refetch();
      }
    };
    $[5] = stackQuery;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const setMenuOpen = t4;
  let t5;
  if ($[7] !== hrefOf || $[8] !== navigate || $[9] !== owner || $[10] !== queryClient || $[11] !== repo || $[12] !== stackQuery.data) {
    t5 = prNumber_0 => {
      setOpen(false);
      seedPrV2Stack(queryClient, owner, repo, String(prNumber_0), stackQuery.data);
      navigate(hrefOf(prNumber_0), {
        source: "stack"
      });
    };
    $[7] = hrefOf;
    $[8] = navigate;
    $[9] = owner;
    $[10] = queryClient;
    $[11] = repo;
    $[12] = stackQuery.data;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const openPullRequest = t5;
  const step = neighbor => {
    const target = stepOriginRef.current?.[neighbor];
    if (target != null) {
      openPullRequest(target);
    }
  };
  const t6 = Boolean(current) && !inSessionPanel;
  let t7;
  if ($[14] !== t6) {
    t7 = {
      enabled: t6,
      enableOnFormTags: false,
      preventDefault: true,
      ignoreEventWhen: _temp2
    };
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const hotkeyOptions = t7;
  let t8;
  if ($[16] !== open || $[17] !== setMenuOpen) {
    t8 = () => setMenuOpen(!open);
    $[16] = open;
    $[17] = setMenuOpen;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  useHotkeys("s", t8, hotkeyOptions);
  let t9;
  if ($[19] !== step) {
    t9 = () => step("parentNumber");
    $[19] = step;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  useHotkeys("bracketleft", t9, hotkeyOptions);
  let t10;
  if ($[21] !== step) {
    t10 = () => step("firstChildNumber");
    $[21] = step;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  useHotkeys("bracketright", t10, hotkeyOptions);
  if (!stackRoot || !current) {
    return null;
  }
  const viewerLogin = typeof user?.user_metadata?.user_name === "string" ? user.user_metadata.user_name : null;
  const bottomOpenEntry = entries.find(_temp3);
  const presentationOf = entry_2 => getPrV2StackStatusPresentation(getPrV2StackStatus(pullRequestsWithReviews.get(entry_2.pullRequest.number) ?? entry_2.pullRequest, {
    viewerLogin,
    isBottomOpenPullRequest: entry_2 === bottomOpenEntry
  }));
  const position = entries.indexOf(current) + 1;
  const showStrip = entries.length <= MAX_STRIP_SEGMENTS;
  const stripBreakpoint = STRIP_BREAKPOINT[inSessionPanel ? "sessionPanel" : "page"];
  const longestNumberLabel = Math.max(...entries.map(_temp4));
  const numberColumnWidth = `${longestNumberLabel}ch`;
  const t11 = "flex justify-end max-md:min-w-0";
  const T0 = Popover;
  const t12 = inSessionPanel ? null : "[";
  const t13 = stepOrigin?.parentNumber == null;
  let t14;
  if ($[23] !== step) {
    t14 = () => step("parentNumber");
    $[23] = step;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  let t16;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = cn(RAIL_BUTTON_CLASS, "w-7 rounded-l-md");
    t16 = <ChevronLeft className="size-4" aria-hidden="true" />;
    $[25] = t15;
    $[26] = t16;
  } else {
    t15 = $[25];
    t16 = $[26];
  }
  let t17;
  if ($[27] !== t13 || $[28] !== t14) {
    t17 = <button type="button" aria-label="Previous PR in stack" disabled={t13} onClick={t14} className={t15}>{t16}</button>;
    $[27] = t13;
    $[28] = t14;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== t12 || $[31] !== t17) {
    t18 = <RailTooltip label="Previous PR in stack" shortcut={t12}>{t17}</RailTooltip>;
    $[30] = t12;
    $[31] = t17;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  const t19 = inSessionPanel ? null : "S";
  const t20 = `Stack, ${position} of ${entries.length}`;
  let t21;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = cn(RAIL_BUTTON_CLASS, "gap-2.5 border-x border-border px-2.5 text-foreground aria-expanded:bg-border");
    $[33] = t21;
  } else {
    t21 = $[33];
  }
  const t22 = showStrip && stripBreakpoint.icon;
  let t23;
  if ($[34] !== t22) {
    t23 = cn("size-3.5 shrink-0", t22);
    $[34] = t22;
    $[35] = t23;
  } else {
    t23 = $[35];
  }
  let t24;
  if ($[36] !== t23) {
    t24 = <Layers className={t23} aria-hidden="true" />;
    $[36] = t23;
    $[37] = t24;
  } else {
    t24 = $[37];
  }
  const t25 = showStrip ? <span aria-hidden="true" data-testid="pr-v2-stack-strip" className={cn("hidden items-center gap-1", stripBreakpoint.strip)}>{entries.map(entry_3 => <span key={entry_3.pullRequest.id} className={cn("rounded-full", entry_3 === current ? "h-1.5 w-5 bg-white" : cn("h-1 w-3", presentationOf(entry_3).segmentClassName))} />)}</span> : null;
  const t26 = `${position}/${entries.length}`;
  let t27;
  if ($[38] !== t26) {
    t27 = <span className="tabular-nums">{t26}</span>;
    $[38] = t26;
    $[39] = t27;
  } else {
    t27 = $[39];
  }
  let t28;
  if ($[40] !== t20 || $[41] !== t24 || $[42] !== t25 || $[43] !== t27) {
    t28 = <PopoverTrigger asChild={true}><button type="button" aria-label={t20} data-testid="pr-v2-stack-dropdown" className={t21}>{t24}{t25}{t27}</button></PopoverTrigger>;
    $[40] = t20;
    $[41] = t24;
    $[42] = t25;
    $[43] = t27;
    $[44] = t28;
  } else {
    t28 = $[44];
  }
  let t29;
  if ($[45] !== t19 || $[46] !== t28) {
    t29 = <RailTooltip label="View stack" shortcut={t19}>{t28}</RailTooltip>;
    $[45] = t19;
    $[46] = t28;
    $[47] = t29;
  } else {
    t29 = $[47];
  }
  const t30 = inSessionPanel ? null : "]";
  const t31 = stepOrigin?.firstChildNumber == null;
  let t32;
  if ($[48] !== step) {
    t32 = () => step("firstChildNumber");
    $[48] = step;
    $[49] = t32;
  } else {
    t32 = $[49];
  }
  let t33;
  let t34;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = cn(RAIL_BUTTON_CLASS, "w-7 rounded-r-md");
    t34 = <ChevronRight className="size-4" aria-hidden="true" />;
    $[50] = t33;
    $[51] = t34;
  } else {
    t33 = $[50];
    t34 = $[51];
  }
  let t35;
  if ($[52] !== t31 || $[53] !== t32) {
    t35 = <button type="button" aria-label="Next PR in stack" disabled={t31} onClick={t32} className={t33}>{t34}</button>;
    $[52] = t31;
    $[53] = t32;
    $[54] = t35;
  } else {
    t35 = $[54];
  }
  let t36;
  if ($[55] !== t30 || $[56] !== t35) {
    t36 = <RailTooltip label="Next PR in stack" shortcut={t30}>{t35}</RailTooltip>;
    $[55] = t30;
    $[56] = t35;
    $[57] = t36;
  } else {
    t36 = $[57];
  }
  let t37;
  if ($[58] !== t18 || $[59] !== t29 || $[60] !== t36) {
    t37 = <div className="flex h-8 items-stretch rounded-md bg-background text-xs font-medium shadow-xs outline outline-border max-md:h-10">{t18}{t29}{t36}</div>;
    $[58] = t18;
    $[59] = t29;
    $[60] = t36;
    $[61] = t37;
  } else {
    t37 = $[61];
  }
  const T1 = PopoverContent;
  const t38 = "end";
  const t39 = -NEXT_BUTTON_WIDTH_PX;
  const t40 = 8;
  const t41 = "Stack";
  const t42 = "max-h-(--radix-popover-content-available-height) w-[min(30rem,calc(100vw-1rem))] overflow-y-auto border-border bg-black p-1.5 text-white";
  let t43;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = event_0 => {
      event_0.preventDefault();
      listRef.current?.querySelector("[aria-current=\"page\"]")?.focus();
    };
    $[62] = t43;
  } else {
    t43 = $[62];
  }
  let t44;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = event_1 => handleLinkListNavigation(event_1, listRef.current);
    $[63] = t44;
  } else {
    t44 = $[63];
  }
  let t45;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <LineDot above={false} below={true} dotClassName="size-2 border border-zinc-600 bg-black" />;
    $[64] = t45;
  } else {
    t45 = $[64];
  }
  let t46;
  if ($[65] !== stackRoot.pullRequest.baseRef) {
    t46 = <div className="flex h-7 items-center pl-1">{t45}<span className="ml-2.5 truncate text-xs text-muted-foreground">{stackRoot.pullRequest.baseRef}</span></div>;
    $[65] = stackRoot.pullRequest.baseRef;
    $[66] = t46;
  } else {
    t46 = $[66];
  }
  const t47 = entries.map(entry_4 => <StackRow key={entry_4.pullRequest.id} entry={entry_4} href={hrefOf(entry_4.pullRequest.number)} isCurrent={entry_4 === current} numberColumnWidth={numberColumnWidth} presentation={presentationOf(entry_4)} onOpen={() => openPullRequest(entry_4.pullRequest.number)} />);
  let t48;
  if ($[67] !== t44 || $[68] !== t46 || $[69] !== t47) {
    t48 = <div ref={listRef} onKeyDown={t44}>{t46}{t47}</div>;
    $[67] = t44;
    $[68] = t46;
    $[69] = t47;
    $[70] = t48;
  } else {
    t48 = $[70];
  }
  let t49;
  if ($[71] !== T1 || $[72] !== t43 || $[73] !== t48) {
    t49 = <T1 align={t38} alignOffset={t39} collisionPadding={t40} aria-label={t41} className={t42} onOpenAutoFocus={t43}>{t48}</T1>;
    $[71] = T1;
    $[72] = t43;
    $[73] = t48;
    $[74] = t49;
  } else {
    t49 = $[74];
  }
  let t50;
  if ($[75] !== T0 || $[76] !== open || $[77] !== setMenuOpen || $[78] !== t37 || $[79] !== t49) {
    t50 = <div className={t11}><T0 open={open} onOpenChange={setMenuOpen}>{t37}{t49}</T0></div>;
    $[75] = T0;
    $[76] = open;
    $[77] = setMenuOpen;
    $[78] = t37;
    $[79] = t49;
    $[80] = t50;
  } else {
    t50 = $[80];
  }
  return t50;
}
function _temp4(t0) {
  const {
    pullRequest: pullRequest_0
  } = t0;
  return `#${pullRequest_0.number}`.length;
}
function _temp3(t0) {
  const {
    pullRequest
  } = t0;
  return pullRequest.state === "open" && !pullRequest.merged;
}
function _temp2(event) {
  return event.repeat;
}
function _temp(entry) {
  return entry.pullRequest;
}